import React, { useState, useRef, useEffect } from 'react';
import './EditarEventos.css'; // Reutilizar estilos existentes
import './EditarEventos-upload.css'; // Estilos de subida
import { ReactComponent as SubirArchivo } from '../assets/icon/subirarchivo.svg';
import { ReactComponent as Volver } from '../assets/icon/volver.svg';
import { useNavigate } from "react-router-dom";
import CalendarInicioEventos from './CalendarInicioEventos';
import CalendarFinEventos from './CalendarFinEventos';
import { eventService, imageService } from '../services';
import NotificationModal from '../Components/NotificationModal';
import { 
  UPLOAD_STATUS, 
  formatDateForAPI,
  parseDate
} from '../utils';

const MAX_FILE_SIZE = 1 * 1024 * 1024;
const MIN_WIDTH = 500;
const MIN_HEIGHT = 500;
const MAX_WIDTH = 1080;
const MAX_HEIGHT = 1080;

const validateImageDimensions = (file) => {
    if (!file) return { isValid: false, errors: ['No se seleccionó ningún archivo'] };
    if (!file.type.startsWith("image/")) {
        return { isValid: false, errors: ['Tipo de archivo no permitido'] };
    }
    if (file.size > MAX_FILE_SIZE) {
        return { isValid: false, errors: ['El archivo no debe exceder 1MB'] };
    }
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const width = img.width;
            const height = img.height;
            const errors = [];
            if (width < MIN_WIDTH || height < MIN_HEIGHT) {
                errors.push(`La imagen debe ser mínimo ${MIN_WIDTH}x${MIN_HEIGHT}px`);
            }
            if (width > MAX_WIDTH || height > MAX_HEIGHT) {
                errors.push(`La imagen debe ser máximo ${MAX_WIDTH}x${MAX_HEIGHT}px`);
            }
            resolve({ isValid: errors.length === 0, errors });
        };
        img.onerror = () => {
            resolve({ isValid: false, errors: ['Error al procesar la imagen'] });
        };
        img.src = URL.createObjectURL(file);
    });
};

const ImageInfoModal = ({ isOpen, onClose }) => {
    const modalRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="info-modal" ref={modalRef}>
                <h3>Medidas de Imagen Soportadas</h3>
                <ul>
                    <li><strong>Ancho:</strong> {MIN_WIDTH} - {MAX_WIDTH} pixels</li>
                    <li><strong>Alto:</strong> {MIN_HEIGHT} - {MAX_HEIGHT} pixels</li>
                    <li><strong>Peso máximo:</strong> 1MB</li>
                    <li><strong>Formatos:</strong> JPEG, PNG</li>
                </ul>
                <button className="modal-close-btn" onClick={onClose}>Cerrar</button>
            </div>
        </div>
    );
};

function AltaEventos() {
    const navigate = useNavigate();

    const [preview, setPreview] = useState(null);
    const [nombre, setNombre] = useState("");
    const [ubicacion, setUbicacion] = useState("");
    const [fechaInicio, setFechaInicio] = useState("");
    const [fechaFin, setFechaFin] = useState("");
    
    const [selectedFile, setSelectedFile] = useState(null);
    const [imageUrl, setImageUrl] = useState("");
    const [imageFileName, setImageFileName] = useState("");
    const [uploadStatus, setUploadStatus] = useState(UPLOAD_STATUS.IDLE);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadError, setUploadError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [notification, setNotification] = useState({ isOpen: false, type: 'success', title: '', message: '' });
    const [showInfo, setShowInfo] = useState(false);

    const handleFile = async (file) => {
        if (!file) return;
        
        try {
            const validation = await validateImageDimensions(file);
            if (!validation.isValid) {
                setUploadError(validation.errors.join(', '));
                setUploadStatus(UPLOAD_STATUS.ERROR);
                return;
            }
            
            setSelectedFile(file);
            setPreview(URL.createObjectURL(file));
            setUploadStatus(UPLOAD_STATUS.IDLE);
            setUploadError("");
            
            // Subir imagen inmediatamente
            setUploadStatus(UPLOAD_STATUS.UPLOADING);
            const result = await imageService.upload(file, {
                onProgress: setUploadProgress,
                skipDuplicate: true
            });
            
            if (result.success) {
                setImageUrl(result.url);
                setImageFileName(result.fileName);
                setUploadStatus(UPLOAD_STATUS.SUCCESS);
            } else {
                throw new Error(result.message || 'Error al subir imagen');
            }
            
        } catch (error) {
            setUploadError(error.message);
            setUploadStatus(UPLOAD_STATUS.ERROR);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        handleFile(e.dataTransfer.files[0]);
    };

    const removeImage = async () => {
        if (imageFileName) {
            try {
                await imageService.deleteImage(imageFileName);
            } catch (error) {
                console.warn('No se pudo eliminar la imagen del servidor:', error.message);
            }
            
            try {
                const cachedHashes = JSON.parse(localStorage.getItem('uploadedImages') || '{}');
                const entriesToDelete = Object.entries(cachedHashes).filter(([key, value]) => value.includes(imageFileName));
                entriesToDelete.forEach(([key]) => {
                    delete cachedHashes[key];
                });
                localStorage.setItem('uploadedImages', JSON.stringify(cachedHashes));
            } catch (error) {
                console.warn('No se pudo limpiar el cache de localStorage:', error.message);
            }
        }
        setPreview(null);
        setSelectedFile(null);
        setImageUrl("");
        setImageFileName("");
        setUploadStatus(UPLOAD_STATUS.IDLE);
        setUploadError("");
        setUploadProgress(0);
    };

    const handleUploadProgress = (progress) => {
        setUploadProgress(progress);
    };

    const extractMonthFromDate = (dateString) => {
        if (!dateString) return null;
        try {
            const date = parseDate(dateString);
            if (!date || isNaN(date.getTime())) return null;
            return date.getMonth() + 1;
        } catch (error) {
            console.warn('Error extracting month from date:', error);
            return null;
        }
    };

    const validateForm = () => {
        if (!fechaInicio) {
            setUploadError("La fecha de inicio es requerida");
            return false;
        }
        
        if (!fechaFin) {
            setUploadError("La fecha de fin es requerida");
            return false;
        }
        
        const fechaInicioDate = new Date(fechaInicio);
        const fechaFinDate = new Date(fechaFin);
        
        if (fechaInicioDate > fechaFinDate) {
            setUploadError("La fecha de inicio no puede ser mayor a la fecha fin");
            return false;
        }
        
        const formData = {
            titulo: nombre,
            ubicacion: ubicacion,
            fechaInicio: fechaInicio,
            fechaFin: fechaFin,
            imagenUrl: imageUrl || preview
        };
        
        const validation = eventService.validateEvent(formData);
        if (!validation.isValid) {
            setUploadError(validation.errors.join(', '));
            return false;
        }
        
        return true;
    };

    const handleCrearEvento = async () => {
        if (!validateForm()) return;
        
        setIsSubmitting(true);
        setUploadError("");
        
        try {
            const fechaInicioFormatted = formatDateForAPI(fechaInicio, 'start');
            const fechaFinFormatted = formatDateForAPI(fechaFin, 'end');
            const mesCalculado = extractMonthFromDate(fechaFin);
            
            const formData = {
                titulo: nombre,
                ubicacion: ubicacion,
                fechaCreacion: fechaInicioFormatted,
                fechaFin: fechaFinFormatted,
                mes: mesCalculado,
                url_File: imageUrl,
                fileName: imageFileName
            };
            
            const result = await eventService.uploadImageAndCreateEvent(
                formData, 
                selectedFile && uploadStatus !== UPLOAD_STATUS.SUCCESS ? selectedFile : null,
                {
                    onImageProgress: handleUploadProgress
                }
            );
            
            if (result.success) {
                setNotification({
                    isOpen: true,
                    type: 'success',
                    title: 'Evento creado',
                    message: result.mensaje || 'El evento se ha creado exitosamente.'
                });
            }
            
        } catch (error) {
            setNotification({
                isOpen: true,
                type: 'error',
                title: 'Error',
                message: error.message || 'No se pudo crear el evento.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleNotificationClose = () => {
        setNotification({ ...notification, isOpen: false });
        if (notification.type === 'success') {
            navigate("/dashboard/Eventos");
        }
    };

    return (
        <div className='bodyAgregarEventos'>

            {/* TÍTULO - Modificado para Alta */}
            <div className='tituloAgregarEventos'><p>Alta de Evento</p></div>
            <div className='cuerpoBotonVolverEventos'>
                <button
                    className='botonesAgregarEventos'
                    onClick={() => navigate("/dashboard/Eventos")}
                >
                    <div className="svgBotones"><Volver className="logo-svgBotones" /></div>
                    Volver
                </button>
            </div>

            <div className='cuerpoAgregarEventos'>

                {/* SUBIR IMAGEN */}
                <div className="logoUpload">
                    <div className="label-with-info">
                        <p className="label">Imagen</p>
                        <button 
                            id="evento-info-btn"
                            className="info-button" 
                            onClick={() => setShowInfo(true)}
                            type="button"
                            title="Ver medidas soportadas"
                        >
                            ?
                        </button>
                    </div>
                    <div
                        className="uploadBox"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                    >
                        {preview ? (
                            <>
                                <img src={preview} alt="preview" className="preview" />

                                {/* BOTONES CAMBIAR / ELIMINAR */}
                                <div className="actions">
                                    <label className="actionButton cambiar">
                                        Cambiar
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleFile(e.target.files[0])}
                                            hidden
                                        />
                                    </label>

                                    <button className="actionButton eliminar" onClick={removeImage}>
                                        Eliminar
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="iconSubirArchivo"><div className="svgSubirArchivo"><SubirArchivo className="logosvgSubirArchivo" /></div></div>
                                <p>Seleccione o arrastre aquí los archivos o</p>

                                <label className="link">
                                    haga click para seleccionar
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleFile(e.target.files[0])}
                                        hidden
                                    />
                                </label>

                                <p>(Peso m&aacute;ximo 1MB)</p>
                            </>
                        )}
                    </div>
                </div>

                {/* FORMULARIO - Simplificado para alta */}
                <div className='inputsEventos'>
                    {/* nombre */}
                    <div className="input-groupAgregarEventos">
                        <div className='text-inputAgregarEventos'>
                            <label htmlFor="TituloAgregarEventos">Titulo</label>
                        </div>
                        <input
                            className="inputAgregarEventos"
                            type="Text"
                            id="text"
                            maxLength={100}
                            value={nombre}
                            onChange={(e) => setNombre(e.target.value)}
                            placeholder="6B Invent Germany"
                        />
                        <div className="contador">{nombre.length}/100</div>
                    </div>
                    
                    {/* Calendario de fecha de inicio */}
                    <div className='calendario'>
                        <CalendarInicioEventos onSelect={setFechaInicio} />
                        <div className="contadorFantasma">0/30</div>
                    </div>

                    {/* Calendario de fecha de fin */}
                    <div className='calendario'>
                        <CalendarFinEventos onSelect={setFechaFin} />
                        <div className="contadorFantasma">0/30</div>
                    </div>

                    {/* ubicacion */}
                    <div className="input-groupAgregarEventos">
                        <div className='text-inputAgregarEventos'>
                            <label htmlFor="UbicacionAgregarEventos">Ubicación</label>
                        </div>
                        <textarea
                            className="AgregarEventostextareaUbicacion"
                            maxLength={250}
                            value={ubicacion}
                            onChange={(e) => setUbicacion(e.target.value)}
                            placeholder="6B Invent Germany"
                            rows={1}
                        />
                        <div className="contador">{ubicacion.length}/250</div>
                    </div>
                    
                    {/* Estado de subida */}
                    {uploadStatus !== UPLOAD_STATUS.IDLE && (
                        <div className="upload-status">
                            {uploadStatus === UPLOAD_STATUS.UPLOADING && (
                                <div className="progress-container">
                                    <div className="progress-bar">
                                        <div 
                                            className="progress-fill" 
                                            style={{ width: `${uploadProgress}%` }}
                                        ></div>
                                    </div>
                                    <p className="progress-text">Subiendo imagen... {uploadProgress}%</p>
                                </div>
                            )}
                            
                            {uploadStatus === UPLOAD_STATUS.SUCCESS && (
                                <div className="success-message">
                                    <p>✓ Imagen subida exitosamente</p>
                                </div>
                            )}
                            
                            {uploadStatus === UPLOAD_STATUS.ERROR && (
                                <div className="error-message">
                                    <p>✗ {uploadError}</p>
                                    <button 
                                        type="button" 
                                        className="retry-button"
                                        onClick={() => setUploadStatus(UPLOAD_STATUS.IDLE)}
                                    >
                                        Reintentar
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Botón único para alta */}
                    <div className='botonesAgregarEventos2'>
                        <button 
                            type="button" 
                            className="btn-acceptGuardarEventos"
                            onClick={handleCrearEvento}
                            disabled={isSubmitting || uploadStatus === UPLOAD_STATUS.UPLOADING}
                            style={{ width: '100%' }}
                        >
                            {isSubmitting ? 'Creando evento...' : 'Crear Evento'}
                        </button>
                    </div>
                </div>

            </div>

            <NotificationModal 
                isOpen={notification.isOpen}
                type={notification.type}
                title={notification.title}
                message={notification.message}
                confirmText="Aceptar"
                onClose={handleNotificationClose}
            />
            <ImageInfoModal isOpen={showInfo} onClose={() => setShowInfo(false)} />
        </div >
    );
}

export default AltaEventos;
